import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import media from 'styled-media-query';
import { useUser } from '../../../../../../redux/user/useUser';
import { PageWrapper } from '../../../../../atoms/PageWrapper';
import { PracticeTitle } from '../PracticeTitle';
import { Breadcrumb } from '../../../../../organisms/Breadcrumb';
import { BasicLayout } from '../../../../../templates/BasicLayout';
import { ForbiddenPage } from '../../../../public/Forbidden';
import { NotFoundPage } from '../../../../public/NotFound';
import { Markdown } from '../../../../../../common/Markdown';
import { LOWER_META_TITLE } from '../../../../../../const/Service';
import { Button } from '../../../../../atoms/Button';
import { Loader } from '../../../../../molecules/Loader';
import { PracticeExplanationFunctions } from './PracticeExplanationFunctions';

export function PracticeExplanation(): React.ReactElement {
  const pathID = useParams<{ practice_id: string }>().practice_id;
  if (!pathID) return NotFoundPage;

  return <Internal practiceID={pathID} />;
}

interface PracticeExplanationProps {
  practiceID: string;
}

function Internal(props: PracticeExplanationProps): React.ReactElement {
  const navigate = useNavigate();

  const { permissionCheck } = useUser();

  const permissions = PracticeExplanationFunctions.getPermissions(permissionCheck);

  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');

  const getPracticeResult = PracticeExplanationFunctions.useGetPractice(props.practiceID, (practice) => {
    setMetaTitle(`${practice?.title} | ${LOWER_META_TITLE}`);
    setMetaDescription(`侍テラコヤの課題ページです。この課題では、${practice?.description}`);
  });

  const practice = getPracticeResult.data?.practice;

  if ((!practice && !getPracticeResult.loading) || practice?.answer === '') {
    return ForbiddenPage;
  }

  return (
    <>
      <Loader display={getPracticeResult.loading} />
      <BasicLayout pageTitle="課題" metaTitle={metaTitle} metaDescription={metaDescription}>
        <PageWrapper>
          <StyledBreadcrumb links={PracticeExplanationFunctions.getBreadcrumbs(practice, permissions)} />
          <Content>
            <StyledPracticeTitle practice={practice} isExplanationTitle />
            <MarkdownWrapper>
              <Markdown content={practice?.answer ?? ''} type="program" />
            </MarkdownWrapper>
          </Content>
          <ButtonArea>
            <Button onClick={() => PracticeExplanationFunctions.onBackButtonClick(props.practiceID, navigate)} border>
              課題詳細に戻る
            </Button>
          </ButtonArea>
        </PageWrapper>
      </BasicLayout>
    </>
  );
}

const StyledBreadcrumb = styled(Breadcrumb)`
  margin-bottom: 1rem;

  ${media.lessThan('medium')`
    display: none;
  `}
`;

const Content = styled.div`
  max-width: 892px;
  margin: auto;

  ${media.lessThan('medium')`
    padding-bottom: 3rem;
  `}
`;

const StyledPracticeTitle = styled(PracticeTitle)`
  ${media.lessThan('medium')`
    margin: -2rem -1rem 0;
  `}
`;

const MarkdownWrapper = styled.div`
  margin: 0 auto;
  padding: 2rem;
  overflow: hidden;

  ${media.lessThan('medium')`
    padding: 1.5rem 0;
  `}
`;

const ButtonArea = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;

  ${media.lessThan('medium')`
    flex-direction: column;
  `}
`;
